/**
 * Global "total unread" sync for the IconRail badge.
 *
 * Mounted from the dashboard layout (`Sidebar.tsx`) so the chat-icon badge
 * stays correct on every dashboard route — not just `/dashboard/inbox`.
 * Without this, `inbox.store.totalUnread` only ever decremented (when the
 * user marked a thread read) and never gained the initial value from the
 * server, so the badge stayed at 0 even with new unread messages waiting.
 *
 * Implementation notes:
 *   - Uses the same `useThreads` hook the inbox page does. React-query
 *     dedupes by query key, so when the user IS on the inbox page there is
 *     ONE underlying HTTP request shared between this hook and the page.
 *   - Filters out CLOSED threads to match the inbox page's `totalUnreadCount`
 *     helper — what the operator sees as "unread" must match what the badge
 *     totals; otherwise the user can have a "1" badge with no row to click.
 *   - Gracefully no-ops for callers without `messages:view` — the query
 *     will 403 in that case, react-query returns no data, and the store
 *     stays at whatever it was. We don't try to gate the call here because
 *     the auth store's permission shape lives elsewhere and we'd be
 *     duplicating that gate; the silent failure is fine.
 */
import { useEffect } from 'react';
import { useThreads } from '@/lib/messaging/hooks';
import { useInboxActions } from '@/stores/inbox.store';
import { useAuthPermissions } from '@/stores/auth.store';

export function useGlobalInboxBadge(): void {
  const { setTotalUnread } = useInboxActions();
  // NOT-504: the Inbox nav is gated on messages:view, so a role without it
  // (e.g. accountant) has no inbox. Skip the badge fetch too — otherwise it
  // fires a guaranteed 403 on every dashboard route for those roles.
  const canViewMessages = useAuthPermissions().includes('messages:view');

  // Match the inbox page's "All" view — OPEN + SNOOZED, no CLOSED. CLOSED
  // threads can still carry unreadCount from before they were closed, but
  // they're hidden from the list view; counting them on the badge would
  // produce a phantom "1" the operator can't reconcile with any visible row.
  const threadsQuery = useThreads({}, { enabled: canViewMessages });

  useEffect(() => {
    const items = threadsQuery.data?.items ?? [];
    const total = items.reduce((sum, t) => sum + (t.unreadCount || 0), 0);
    setTotalUnread(total);
  }, [threadsQuery.data, setTotalUnread]);
}
